/**
 * Entity display-name resolution — the LexIDE consumer foundation for the
 * entity-identity model (ai-docs/entity-identity-slug-displaynames-plan.md §6).
 *
 * Every hierarchy entity (Client / Matter / Case) will carry an i18n
 * `displayNames { pt, en, es }` (mutable, the human label) alongside its
 * immutable `id` + `slug`. This helper renders the right label for the active
 * locale with a fail-soft fallback chain, so a component can adopt it as a
 * ZERO-VISIBLE-CHANGE refactor NOW (it falls back to the existing `name`) and
 * light up automatically the moment the backend starts returning `displayNames`
 * (Slice 1 — see the backend relay 2026-07-08).
 *
 * Slice 0 (this file): the pure resolver + a React hook. No component wiring
 * yet — that lands per-surface as the backend contract ships.
 */

import type { LanguagePreference } from "@/lib/language";
import { useLanguageStore } from "@/stores/useLanguageStore";

/** The i18n label map an entity carries (plan §3). All optional — absent until
 *  the backend ships the field, at which point 1–3 locales are populated. */
export interface DisplayNames {
  pt?: string | null;
  en?: string | null;
  es?: string | null;
}

/** Minimal identity shape the resolver reads. Every field optional so it works
 *  BEFORE the backend ships `displayNames`/`slug` (fail-soft to name → slug → id). */
export interface DisplayableEntity {
  displayNames?: DisplayNames | null;
  name?: string | null;
  slug?: string | null;
  id?: string | null;
}

/** Map a BCP-47 tag (or short code) to the display-name locale key. Unknown →
 *  `pt` (the platform default + the seeded fallback locale). Mirrors
 *  `case-labels.bcp47ToShort`. */
function displayLocale(locale: LanguagePreference | string): "pt" | "en" | "es" {
  const s = String(locale);
  if (s.startsWith("en")) return "en";
  if (s.startsWith("es")) return "es";
  return "pt";
}

/** A trimmed non-empty string, or undefined. */
function nonEmpty(v: string | null | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

/**
 * Resolve the best display string for an entity, per the entity-identity model.
 *
 * Fallback chain (plan §6):
 *   displayNames[locale] → displayNames.pt → displayNames.en → displayNames.es
 *   → name → slug → id → "".
 *
 * `locale` accepts a short code (`"pt"`/`"en"`/`"es"`) or a BCP-47 tag
 * (`"pt-BR"`/`"en-US"`/`"es-ES"`). Whitespace-only values are treated as absent.
 */
export function resolveDisplayName(
  entity: DisplayableEntity | null | undefined,
  locale: LanguagePreference | string,
): string {
  if (!entity) return "";
  const dn = entity.displayNames;
  if (dn) {
    const loc = displayLocale(locale);
    const localized = nonEmpty(dn[loc]) ?? nonEmpty(dn.pt) ?? nonEmpty(dn.en) ?? nonEmpty(dn.es);
    if (localized) return localized;
  }
  return nonEmpty(entity.name) ?? nonEmpty(entity.slug) ?? nonEmpty(entity.id) ?? "";
}

/**
 * STRICT sibling of `resolveDisplayName` — the same chain, stopping BEFORE the
 * identifier fallbacks, and returning `undefined` when no human label exists.
 *
 *   displayNames[locale] → displayNames.pt → .en → .es → name → **undefined**
 *
 * ## Why this exists (breadcrumb redesign B-3, `lexide-breadcrumb-case-summary-redesign-plan.md` §3.2.1)
 *
 * The lenient resolver above ends `… → name → slug → id → ""`, which is correct
 * for its original callers (a label that degrades to *something* beats a blank)
 * but is a **leak** anywhere the output reaches lawyer-facing copy: the standing
 * invariant is that no raw entity id may be rendered to a lawyer. Calling the
 * lenient version in a breadcrumb resolver type-checks, passes review, and still
 * prints `client-mrc6c8xk-5ajbya`.
 *
 * ⚠ **The subtle half — a caller-side `|| fallback` does NOT save you.** A
 * returned id is a non-empty string, so it is truthy, so the `||` never fires in
 * exactly the case it was written for:
 *
 * ```ts
 * // reads as "fall back to a generic label"; actually renders the matter ID,
 * // because resolveDisplayName already returned one and short-circuited the ||
 * resolveDisplayName(matter, lang) || "(matéria sem nome)"
 * ```
 *
 * So the `||` guard is unreachable dead code whenever the entity carries an id.
 * Use THIS function plus an explicit `?? genericLabel` instead — `undefined` is
 * falsy in a way an id never is, and it makes the generic-label branch reachable.
 *
 * Callers that legitimately want the id (diagnostics, `data-*` attributes, the
 * context popover's explicit "what the AI has in context" list) keep using the
 * lenient one — the invariant is *no id in lawyer-facing copy*, not *no id may
 * exist*.
 */
export function resolveDisplayNameStrict(
  entity: DisplayableEntity | null | undefined,
  locale: LanguagePreference | string,
): string | undefined {
  if (!entity) return undefined;
  const dn = entity.displayNames;
  if (dn) {
    const loc = displayLocale(locale);
    const localized = nonEmpty(dn[loc]) ?? nonEmpty(dn.pt) ?? nonEmpty(dn.en) ?? nonEmpty(dn.es);
    if (localized) return localized;
  }
  // Deliberately NOT `?? slug ?? id` — that is the whole point of this variant.
  return nonEmpty(entity.name);
}

/**
 * React hook — resolves an entity's display name for the active locale (read
 * from `useLanguageStore`). Re-renders on locale change. Prefer this in
 * components; use `resolveDisplayName` directly in non-React contexts.
 */
export function useDisplayName(entity: DisplayableEntity | null | undefined): string {
  const lang = useLanguageStore((s) => s.lang);
  return resolveDisplayName(entity, lang);
}
